// components/auth/LandingRedirect.tsx
/**
 * The `/` index route. Resolves the active account's landing destination to a
 * concrete visible leaf route and redirects there.
 *
 * This is the single resolution point for "go home": every post-auth flow
 * (login redirect, magic-link, invite-accept, catch-all, the MenuGuard "hidden"
 * fallback) navigates to `/` and lands here, where the auth context is fresh.
 * That dodges the stale-closure problem in async flows that navigate right after
 * switchAccount(), and centralises the visibility-aware fallback logic.
 *
 * While the account is still loading we render the page loader rather than
 * redirecting — resolving against a null account would pick the wrong target.
 */

import { Navigate } from "@/routing/accountRouting";
import { useAuth } from "@/context/AuthContext";
import { useLandingPath } from "@/hooks/useLandingPath";
import { PageLoader } from "@/components/loaders/PageLoader";

export default function LandingRedirect() {
  const { activeAccount } = useAuth();
  const landingPath = useLandingPath();

  // Authenticated but the account context hasn't hydrated yet — wait, don't
  // resolve against a null account.
  if (!activeAccount) {
    return <PageLoader dark={false} />;
  }

  return <Navigate to={landingPath} replace />;
}
